import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { useT } from '../i18n';
import TimezonePicker from './TimezonePicker';
import { COUNTRY_GROUPS, COUNTRIES, getCountryName, getGroupLabel } from '../data/countries';

const LANGUAGES = [
  { value: 'it',    flag: '🇮🇹', label: 'Italiano' },
  { value: 'en-US', flag: '🇺🇸', label: 'English (US)' },
  { value: 'en-GB', flag: '🇬🇧', label: 'English (UK)' },
  { value: 'de',    flag: '🇩🇪', label: 'Deutsch' },
  { value: 'fr',    flag: '🇫🇷', label: 'Français' },
  { value: 'nl',    flag: '🇳🇱', label: 'Nederlands' },
  { value: 'pt-PT', flag: '🇵🇹', label: 'Português (PT)' },
  { value: 'pt-BR', flag: '🇧🇷', label: 'Português (BR)' },
  { value: 'es-ES', flag: '🇪🇸', label: 'Español (ES)' },
  { value: 'es-AR', flag: '🇦🇷', label: 'Español (AR)' },
  { value: 'es-MX', flag: '🇲🇽', label: 'Español (MX)' },
  { value: 'es-CO', flag: '🇨🇴', label: 'Español (CO)' },
  { value: 'ru',    flag: '🇷🇺', label: 'Русский' },
  { value: 'pl',    flag: '🇵🇱', label: 'Polski' },
  { value: 'ro',    flag: '🇷🇴', label: 'Română' },
  { value: 'zh',    flag: '🇨🇳', label: '中文' },
  { value: 'ja',    flag: '🇯🇵', label: '日本語' },
  { value: 'hi',    flag: '🇮🇳', label: 'हिन्दी' },
  { value: 'ko',    flag: '🇰🇷', label: '한국어' },
  { value: 'ar',    flag: '🇸🇦', label: 'العربية' },
];

const SUPPORTED_LANGS = LANGUAGES.map((l) => l.value);

// The list above holds full codes for the languages that have variants
// ('en-US', 'es-ES', 'pt-BR'…), so the old two-letter check could never match
// them: an English, Spanish or Portuguese phone came out as 'en' — which is not
// in the list either. The dropdown opened on a value with no matching option,
// and whoever did not notice had 'en' saved as their language. The first screen
// of the app, in the wrong language, for the three biggest markets.
const BASE_TO_FULL = { en: 'en-US', es: 'es-ES', pt: 'pt-PT' };

// Shown when the setup could not be saved. Kept here rather than in i18n
// because at this moment the chosen language may not have reached the server.
const SAVE_FAILED_MSG = {
  it: 'Non sono riuscita a salvare. Controlla la connessione e premi di nuovo: senza queste risposte MeMo partirebbe con la lingua e il fuso orario sbagliati.',
  en: "Couldn't save. Check your connection and tap again: without these answers MeMo would start with the wrong language and time zone.",
  es: 'No he podido guardar. Comprueba la conexión y vuelve a pulsar: sin estas respuestas MeMo empezaría con el idioma y la zona horaria equivocados.',
  de: 'Speichern fehlgeschlagen. Prüfe die Verbindung und tippe erneut: ohne diese Angaben startet MeMo mit falscher Sprache und Zeitzone.',
  fr: "L'enregistrement a échoué. Vérifiez la connexion et réessayez : sans ces réponses, MeMo démarrerait avec la mauvaise langue et le mauvais fuseau horaire.",
  ru: "Не удалось сохранить. Проверьте соединение и нажмите ещё раз: без этих ответов MeMo запустится с неправильным языком и часовым поясом.",
  pl: "Nie udało się zapisać. Sprawdź połączenie i naciśnij ponownie: bez tych odpowiedzi MeMo wystartuje z błędnym językiem i strefą czasową.",
  ro: "Nu am putut salva. Verifică conexiunea și apasă din nou: fără aceste răspunsuri MeMo ar porni cu limba și fusul orar greșite.",
  zh: "保存失败。请检查网络后再点一次：没有这些设置，MeMo 会以错误的语言和时区启动。",
  ja: "保存できませんでした。通信を確認してもう一度押してください。この設定がないと、MeMo は間違った言語とタイムゾーンで起動します。",
  hi: "सहेजा नहीं जा सका। कनेक्शन जाँचकर दोबारा दबाएँ: इन जवाबों के बिना MeMo ग़लत भाषा और समय क्षेत्र के साथ शुरू होगा।",
  ko: "저장하지 못했습니다. 연결을 확인하고 다시 누르세요. 이 설정이 없으면 MeMo가 잘못된 언어와 시간대로 시작됩니다.",
  ar: "تعذّر الحفظ. تحقّق من الاتصال واضغط مرة أخرى: بدون هذه الإجابات سيبدأ MeMo بلغة ومنطقة زمنية خاطئتين.",
  nl: 'Opslaan is mislukt. Controleer je verbinding en tik opnieuw: zonder deze antwoorden start MeMo met de verkeerde taal en tijdzone.',
  pt: 'Não consegui guardar. Verifica a ligação e tenta de novo: sem estas respostas o MeMo começaria com o idioma e o fuso horário errados.',
  'pt-BR': 'Não consegui salvar. Verifique a conexão e tente de novo: sem essas respostas o MeMo começaria com o idioma e o fuso horário errados.',
};

function detectBrowserLang() {
  const nav = navigator.language || '';
  const exact = SUPPORTED_LANGS.find((l) => l.toLowerCase() === nav.toLowerCase());
  if (exact) return exact;                       // 'pt-BR' → 'pt-BR'
  const base = nav.split('-')[0].toLowerCase();
  return BASE_TO_FULL[base]                      // 'es-MX' → 'es-ES'
    || SUPPORTED_LANGS.find((l) => l === base)   // 'de-AT' → 'de'
    || 'en-US';
}

function getLangFlag(langCode) {
  const lang = LANGUAGES.find((l) => l.value === langCode);
  return lang ? lang.flag : '';
}

function getLangLabel(langCode) {
  const lang = LANGUAGES.find((l) => l.value === langCode);
  return lang ? lang.label : langCode;
}

export default function WelcomeScreen() {
  const { user, setUser } = useAuth();
  const [selectedCountry, setSelectedCountry] = useState(null);
  // Pre-fill with user's saved language from DB (persists across incognito sessions)
  const [language, setLanguage] = useState(() => user?.language || detectBrowserLang());
  const [timezone, setTimezone] = useState(() => {
    // Always auto-detect from the browser — ignore the DB default of Europe/Rome
    // so users in Brazil, US, Japan etc. get the right timezone without doing anything
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return detected || user?.timezone || 'Europe/Rome';
  });
  const [theme, setTheme] = useState('light');
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const T = useT(language);

  function handleCountryChange(e) {
    const code = e.target.value;
    if (!code) {
      setSelectedCountry(null);
      return;
    }
    const country = COUNTRIES.find((c) => c.code === code);
    if (country) {
      setSelectedCountry(country);
      setLanguage(country.language);
      setTimezone(country.timezone);
    }
  }

  function handleThemeChange(value) {
    setTheme(value);
    document.documentElement.className = value === 'dark' ? 'theme-dark' : '';
  }

  async function handleContinue() {
    setSaving(true);
    setSaveFailed(false);
    try {
      await apiClient.put('/settings', { language, timezone, theme });
    } catch (err) {
      // Do NOT walk on. Marking the setup done while the answers were lost is
      // the one mistake this screen cannot recover from: it is never shown
      // again, so the language and the time zone stay wrong for good — and a
      // wrong time zone means the morning summary arriving in the middle of the
      // night, with nothing on screen to explain why.
      setSaving(false);
      setSaveFailed(true);
      return;
    }
    document.documentElement.className = theme === 'dark' ? 'theme-dark' : '';
    localStorage.setItem('onboarded_' + user.id, '1');
    // Show the guided intro slides right after setup (only for this new user).
    localStorage.setItem('tour_pending_' + user.id, '1');
    window.location.href = '/';
  }

  const themeButtonBase = {
    flex: 1,
    padding: '8px 10px',
    borderRadius: 'var(--radius-sm)',
    border: '2px solid var(--color-border)',
    background: 'var(--color-card)',
    color: 'var(--color-text)',
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  };

  const themeButtonSelected = {
    border: '2px solid var(--color-primary)',
    background: 'rgba(122,158,126,0.1)',
    color: 'var(--color-primary-dark)',
  };

  return (
    <div className="login-page" style={{ overflowY: 'auto', justifyContent: 'flex-start', paddingTop: 48 }}>
      <div className="login-logo" style={{ fontSize: 'inherit', background: 'none' }}>
        <span style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontWeight: 900,
          fontSize: '2.2rem',
          background: 'linear-gradient(135deg, #1a4a35 0%, #52b788 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          letterSpacing: '2px',
          display: 'inline-flex',
          alignItems: 'center',
        }}>
          M<span style={{ fontSize: '2.6rem' }}>e</span>M
          <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ display: 'inline-block', verticalAlign: 'middle', marginInlineStart: 1 }}>
            <circle cx="12" cy="12" r="10.2" stroke="#1a4a35" strokeWidth="2.8" fill="none"/>
            <line x1="12" y1="12" x2="12" y2="5" stroke="#1a4a35" strokeWidth="2.4" strokeLinecap="round"/>
            <line x1="12" y1="12" x2="17" y2="15" stroke="#52b788" strokeWidth="2.2" strokeLinecap="round"/>
          </svg>
        </span>
      </div>
      <h1 className="login-title">{`${T.welcome.greeting} ${user?.name?.split(' ')[0]}!`}</h1>
      <p className="login-subtitle">{T.welcome.subtitle}</p>

      <div style={{ width: '100%', maxWidth: 320 }}>

        {/* Country dropdown */}
        <div style={{ marginBottom: 20 }}>
          <label style={{
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--color-text-muted)',
            marginBottom: 8,
          }}>
            {T.welcome.countryLabel}
          </label>
          <select
            className="form-select"
            value={selectedCountry?.code || ''}
            onChange={handleCountryChange}
            style={{ width: '100%' }}
          >
            <option value="">{T.welcome.countryPlaceholder}</option>
            {COUNTRY_GROUPS.map((group) => (
              <optgroup key={group.group_en} label={getGroupLabel(group, language)}>
                {group.countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {getCountryName(country, language)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          {selectedCountry && (
            <div style={{
              marginTop: 8,
              padding: '8px 12px',
              background: 'var(--color-surface)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.8rem',
              color: 'var(--color-text-muted)',
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
            }}>
              <span>🌍 {getCountryName(selectedCountry, language)} ({getGroupLabel(COUNTRY_GROUPS.find(g => g.countries.some(c => c.code === selectedCountry.code)), language)})</span>
              <span>→</span>
              <span>{getLangFlag(selectedCountry.language)} {getLangLabel(selectedCountry.language)}</span>
              <span>🕐 {selectedCountry.timezone}</span>
            </div>
          )}
        </div>

        {/* Language override pills */}
        <div style={{ marginBottom: 20 }}>
          <label style={{
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--color-text-muted)',
            marginBottom: 8,
          }}>
            {T.settings.language}
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.value}
                type="button"
                onClick={() => setLanguage(lang.value)}
                style={{
                  padding: '10px 6px',
                  borderRadius: 'var(--radius-sm)',
                  border: `2px solid ${language === lang.value ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  background: language === lang.value ? 'rgba(122,158,126,0.1)' : 'var(--color-card)',
                  color: language === lang.value ? 'var(--color-primary-dark)' : 'var(--color-text)',
                  fontWeight: language === lang.value ? 700 : 500,
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  textAlign: 'center',
                }}
              >
                {lang.flag}<br/><span style={{fontSize:'0.7rem'}}>{lang.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Timezone picker */}
        <div style={{ marginBottom: 20 }}>
          <label style={{
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--color-text-muted)',
            marginBottom: 8,
          }}>
            {T.welcome.timezoneLabel}
          </label>
          {/* Same picker as Settings. The very first screen a person sees
              should not be the one place still showing a grey list box. */}
          <TimezonePicker
            value={timezone}
            language={language}
            ariaLabel={T.welcome.timezoneLabel}
            onChange={setTimezone}
          />
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 6 }}>
            {T.welcome.timezoneHint}
          </p>
        </div>

        {/* Theme picker */}
        <div style={{ marginBottom: 20 }}>
          <label style={{
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--color-text-muted)',
            marginBottom: 8,
          }}>
            {T.welcome.themeLabel}
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={() => handleThemeChange('light')}
              style={{
                ...themeButtonBase,
                ...(theme === 'light' ? themeButtonSelected : {}),
              }}
            >
              <span style={{ fontSize: '1rem' }}>☀️</span>
              <span>{T.settings.themeLight}</span>
            </button>
            <button
              type="button"
              onClick={() => handleThemeChange('dark')}
              style={{
                ...themeButtonBase,
                ...(theme === 'dark' ? themeButtonSelected : {}),
              }}
            >
              <span style={{ fontSize: '1rem' }}>🌙</span>
              <span>{T.settings.themeDark}</span>
            </button>
          </div>
        </div>

        {/* Continue button */}
        {saveFailed && (
          <div className="alert alert-error" style={{ marginBottom: 10, fontSize: '0.85rem', lineHeight: 1.45 }}>
            {SAVE_FAILED_MSG[language]
              || SAVE_FAILED_MSG[String(language).split('-')[0]]
              || SAVE_FAILED_MSG.en}
          </div>
        )}
        <button
          className="btn btn-primary"
          onClick={handleContinue}
          disabled={saving}
        >
          {saving ? T.welcome.saving : T.welcome.continue}
        </button>
      </div>
    </div>
  );
}
